import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { makeStyles } from "tss-react/mui";
import { Box } from "@mui/material";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import MessagesList from "../MessagesList";
import TicketMessagesSearch from "../TicketMessagesSearch";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { SearchMessageProvider } from "../../context/SearchMessage/SearchMessageContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()(() => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    setSearchOpen(false);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);
          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, navigate]);

  useEffect(() => {
    const socket = openSocket();
    const numericTicketId = +ticketId;

    const handleConnect = () => socket.emit("joinChatBox", ticketId);

    const handleTicket = (data) => {
      if (data.ticket?.id !== numericTicketId && data.ticketId !== numericTicketId) return;
      if (data.action === "update") setTicket(data.ticket);
      if (data.action === "delete") navigate("/tickets");
    };

    const handleContact = (data) => {
      if (data.action === "update") {
        setContact((prev) =>
          prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev
        );
      }
    };

    // The socket is a shared singleton — it's usually already connected when
    // this page mounts, so "connect" would never fire. Join immediately, and
    // keep the listener to re-join after reconnects.
    if (socket.connected) handleConnect();
    socket.on("connect", handleConnect);
    socket.on("ticket", handleTicket);
    socket.on("contact", handleContact);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("ticket", handleTicket);
      socket.off("contact", handleContact);
    };
  }, [ticketId, navigate]);

  return (
    <div className={classes.root} id="drawer-container">
      <Box className={classes.mainWrapper} sx={{ bgcolor: "background.paper" }}>
        <TicketHeader
          loading={loading}
          contact={contact}
          ticket={ticket}
          onContactClick={() => setDrawerOpen(true)}
          onSearchClick={() => setSearchOpen((open) => !open)}
        />
        <ReplyMessageProvider>
          <SearchMessageProvider>
            {searchOpen && (
              <TicketMessagesSearch
                ticketId={ticketId}
                onClose={() => setSearchOpen(false)}
              />
            )}
            <MessagesList ticketId={ticketId} isGroup={ticket.isGroup} />
            <MessageInput ticketStatus={ticket.status} />
          </SearchMessageProvider>
        </ReplyMessageProvider>
      </Box>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={() => setDrawerOpen(false)}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
